/**
 * hooks/useTenantBrand.ts
 *
 * Fetches the active tenant's brand_config_json from tenant_settings,
 * deep-merges it with DEFAULT_BRAND, and returns the resolved BrandConfig.
 *
 * Used in the app shell to pass the config to <BrandProvider>.
 *
 * @example
 * function AppShell() {
 *   const { brand, isLoading } = useTenantBrand()
 *   return (
 *     <BrandProvider config={brand}>
 *       <Outlet />
 *     </BrandProvider>
 *   )
 * }
 */

import { useQuery }      from '@tanstack/react-query'
import { useSupabase }   from './useSupabase'
import { DEFAULT_BRAND } from '@/lib/brand/config'
import type { BrandConfig } from '@/lib/brand/types'

// ---------------------------------------------------------------------------
// Deep merge utility (partial override onto base)
// ---------------------------------------------------------------------------

function deepMerge<T extends object>(base: T, override: Partial<T>): T {
  const result: T = { ...base }
  for (const key of Object.keys(override) as (keyof T)[]) {
    const val = override[key]
    if (val !== null && typeof val === 'object' && !Array.isArray(val) &&
        typeof base[key] === 'object' && base[key] !== null) {
      result[key] = deepMerge(base[key] as object, val as object) as T[typeof key]
    } else if (val !== undefined) {
      result[key] = val as T[typeof key]
    }
  }
  return result
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

interface UseTenantBrandResult {
  brand:     BrandConfig
  isLoading: boolean
}

export function useTenantBrand(): UseTenantBrandResult {
  const supabase = useSupabase()

  const { data, isLoading } = useQuery({
    queryKey:  ['tenant-brand'],
    queryFn:   async () => {
      const { data, error } = await supabase
        .from('tenant_settings')
        .select('brand_config_json')
        .single()

      if (error) throw error
      return data?.brand_config_json as Partial<BrandConfig> | null
    },
    staleTime: 5 * 60 * 1000,   // 5 min — brand rarely changes
    retry:     2,
  })

  const brand = data ? deepMerge<BrandConfig>(DEFAULT_BRAND, data) : DEFAULT_BRAND

  return { brand, isLoading }
}
